import { QueryCache, MutationCache, type Query } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { logSystemError, classifyError, isRLSError, incrementCrashCount } from '../components/ui/ErrorMonitor';
import { isPermissionError, browserOnline } from './reports/reportState';

/**
 * WIRING-2 — a failed query must never be mistaken for "no data".
 *
 * Measured on Production: 55 routed pages render a list from `useQuery` and read
 * nothing but `data`, so a 400 (a column the table does not have), a 403 (RLS or a
 * revoked EXECUTE) or a network failure leaves the same empty table a genuinely
 * empty company would show. The global QueryCache is the one place every query
 * passes through, so the surfacing lives here: the first failed load of a query
 * that has NOTHING on screen tells the user what happened — permission, offline,
 * or server — in their language. A refetch that fails while stale rows are still
 * displayed is logged (below) but not shouted; the rows stay put.
 *
 * Pages that already render their own error block opt out per query with
 * `meta: { surfaceError: false }` (the finance reports do this through
 * ReportStates). Duplicate toasts for the same key are suppressed for 30 s, and a page
 * whose every query fails at once (the whole API unreachable, a revoked role) shows one
 * toast per distinct message rather than one per query: the toast id is the message
 * class, so react-hot-toast replaces instead of stacking.
 */
const SURFACE_WINDOW_MS = 30_000;
const lastSurfaced = new Map<string, number>();

type FailureClass = 'denied' | 'offline' | 'error';

function classifyFailure(error: unknown): FailureClass {
  if (isPermissionError(error)) return 'denied';
  if (browserOnline() === false) return 'offline';
  return 'error';
}

/** The slice of a Query the surfacing needs; structural so the cache's own generics do not matter. */
export type SurfaceableQuery = {
  queryKey: Query['queryKey'];
  meta: Query['meta'];
  state: { data?: unknown };
};

export function surfaceQueryFailure(error: unknown, query: SurfaceableQuery): void {
  if (query.meta?.surfaceError === false) return;
  if (query.state.data !== undefined) return;
  const key = JSON.stringify(query.queryKey);
  const now = Date.now();
  if (now - (lastSurfaced.get(key) ?? 0) < SURFACE_WINDOW_MS) return;
  lastSurfaced.set(key, now);

  const cls = classifyFailure(error);
  const message =
    cls === 'denied'
      ? i18n.t('rpt_denied', { defaultValue: 'You do not have permission to view this data for this company' })
      : cls === 'offline'
        ? i18n.t('rpt_offline', { defaultValue: 'No network connection — the data was not requested' })
        : i18n.t('rpt_error', { defaultValue: 'Failed to load data' });
  toast.error(message, { id: `query-failure:${cls}` });
}

/** Test seam: forget the de-duplication window. */
export function __resetSurfacedQueryFailures(): void {
  lastSurfaced.clear();
}

/**
 * Extracts a module name from a React Query key.
 * Query keys typically follow the pattern: ['entity-name', companyId].
 */
function moduleFromKey(queryKey: readonly unknown[]): string {
  const first = queryKey[0];
  if (typeof first === 'string') return first;
  return 'unknown';
}

/**
 * Shared handler for both query and mutation errors.
 */
function handleError(error: unknown, queryKey: readonly unknown[]): void {
  const module = moduleFromKey(queryKey);
  const errorType = classifyError(error);
  const errorMessage =
    typeof error === 'string'
      ? error
      : error instanceof Error
        ? error.message
        : 'Unknown error';

  // Track crash count per module
  incrementCrashCount(module);

  // Log to system_error_logs (non-blocking)
  logSystemError({
    module,
    page: typeof window !== 'undefined' ? window.location.pathname : '',
    error_type: errorType,
    error_message: errorMessage.slice(0, 2000),
  });

  // Extra console warning for RLS errors (useful during development)
  if (isRLSError(error)) {
    console.warn(
      `[ErrorMonitor] RLS denial detected on "${module}":`,
      errorMessage,
    );
  }
}

/**
 * Creates a QueryCache with a global onError handler that auto-logs
 * all Supabase query errors to the system_error_logs table.
 *
 * Usage in queryClient.ts:
 *   import { createMonitoredQueryCache, createMonitoredMutationCache } from './queryErrorHandler';
 *   export const queryClient = new QueryClient({
 *     queryCache: createMonitoredQueryCache(),
 *     mutationCache: createMonitoredMutationCache(),
 *     defaultOptions: { queries: { retry: 1, staleTime: 30000 } },
 *   });
 */
export function createMonitoredQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error, query) => {
      handleError(error, query.queryKey);
      surfaceQueryFailure(error, query);
    },
  });
}

/**
 * Creates a MutationCache with a global onError handler that auto-logs
 * all Supabase mutation errors to the system_error_logs table.
 */
export function createMonitoredMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const key = mutation.options.mutationKey ?? ['mutation'];
      handleError(error, key as readonly unknown[]);
    },
  });
}
